'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Construction } from 'lucide-react';
import { AppGateShell } from '@/components/AppGateShell';

const STORAGE_KEY = 'mineme.maintenance.cache';

/** Survive remounts / Fast Refresh so we don't flash the full-screen loader again. */
let maintenanceCache = {
  checked: false,
  on: false,
  message: '',
};

function readStoredCache() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      checked: true,
      on: !!parsed.on,
      message: typeof parsed.message === 'string' ? parsed.message : '',
    };
  } catch {
    return null;
  }
}

function writeStoredCache(next) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ on: next.on, message: next.message })
    );
  } catch {
    /* ignore */
  }
}

function hydrateCache() {
  if (maintenanceCache.checked) return maintenanceCache;
  const stored = readStoredCache();
  if (stored) {
    maintenanceCache = stored;
  }
  return maintenanceCache;
}

function isMaintenanceExemptPath(path) {
  if (path.startsWith('/admin')) return true;
  if (path === '/user' || path === '/user/') return true;
  if (path === '/user/signup' || path === '/user/forgot-password') return true;
  if (path === '/agent' || path === '/agent/') return true;
  if (path === '/agent/signup' || path === '/agent/forgot-password') return true;
  return false;
}

export default function MaintenanceGate({ children }) {
  const router = useRouter();
  const path = router.pathname || '';
  const boot = hydrateCache();
  /**
   * Never block first paint with BrandOrbitLoader on hard URL loads.
   * In-app nav already has cache; hard reload used to stick on that spinner
   * while /api/public/maintenance waited on Mongo — admin skips this entirely.
   */
  const [state, setState] = useState(() =>
    boot.checked
      ? { loading: false, on: boot.on, message: boot.message }
      : { loading: false, on: false, message: '' }
  );

  useEffect(() => {
    if (isMaintenanceExemptPath(path)) {
      setState((prev) =>
        prev.on ? { loading: false, on: false, message: '' } : prev
      );
      return;
    }

    let cancelled = false;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = setTimeout(() => controller?.abort(), 4000);

    const applyOk = (data = {}) => {
      if (cancelled) return;
      const next = {
        loading: false,
        on: !!data.maintenanceMode,
        message: typeof data.message === 'string' ? data.message : '',
      };
      maintenanceCache = {
        checked: true,
        on: next.on,
        message: next.message,
      };
      writeStoredCache(next);
      setState((prev) =>
        prev.loading === next.loading &&
        prev.on === next.on &&
        prev.message === next.message
          ? prev
          : next
      );
    };

    const applyFailOpen = () => {
      if (cancelled) return;
      const next = { loading: false, on: false, message: '' };
      maintenanceCache = { checked: true, on: false, message: '' };
      writeStoredCache(next);
      setState((prev) => (prev.on ? next : { ...prev, loading: false }));
    };

    fetch('/api/public/maintenance', {
      cache: 'no-store',
      signal: controller?.signal,
    })
      .then((r) => r.json())
      .then((data) => applyOk(data))
      .catch(() => applyFailOpen())
      .finally(() => clearTimeout(timeoutId));

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller?.abort();
    };
  }, [path]);

  if (isMaintenanceExemptPath(path)) {
    return children;
  }

  if (state.on) {
    return (
      <AppGateShell busy={false}>
        <div className="mx-auto mb-8 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-linear-to-br from-accent-amber to-accent-orange shadow-lg shadow-accent-amber/20">
          <Construction className="h-8 w-8 text-black" strokeWidth={2} />
        </div>
        <p className="font-display text-6xl leading-none text-ember sm:text-7xl">503</p>
        <h1 className="font-display mt-4 text-2xl text-text-primary sm:text-3xl">Under maintenance</h1>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-text-muted sm:text-base">
          {state.message.trim() ||
            'The platform is temporarily unavailable while we make improvements. Please check again soon.'}
        </p>
      </AppGateShell>
    );
  }

  return children;
}
